<!--
  A WAI-ARIA tabs widget: one tablist, one tabpanel per tab, one panel
  visible at a time. Documents.vue built the first tablist in-page and said
  the extraction belonged with a second consumer; Dispatch and Billing are
  the second and third, so this is that extraction.

  The pattern, in full (https://www.w3.org/WAI/ARIA/apg/patterns/tabs/):
  role=tablist / tab / tabpanel, aria-selected, aria-controls and
  aria-labelledby tying each tab to its panel, and a roving tabindex so Tab
  moves into the list once and out again. Left/Right move between tabs and
  wrap; Home/End jump to the ends. Selection follows focus (automatic
  activation): the panels here are already rendered, so showing one costs
  nothing and there is no reason to make a keyboard user press Enter too.

  Controlled: `modelValue` is the selected tab's id and the component never
  holds a copy of it. A page that keeps the selection in the URL (Dispatch,
  Billing) owns that; this component only asks for a change.

  Panels are slots named after each tab's id and hide with v-show, not
  v-if. Every panel stays mounted, so whatever a panel fetched, filtered or
  selected survives switching away and back, and a panel that listens for
  another panel's changes keeps listening while hidden.

  A tab's `count` is optional and renders as a small badge. It is a number
  the page already has, not a reason to fetch one; null or undefined shows
  no badge at all, and 0 shows "0".
-->
<template>
  <div class="lw-c-tabs" :class="{ 'lw-c-tabs--pills': nested }">
    <div ref="scroller" class="lw-c-tabs__list-scroller">
      <div class="lw-c-tabs__list" role="tablist" :aria-label="label" @keydown="handleKeydown">
        <button
          v-for="tab in tabs"
          :id="tabId(tab.id)"
          :key="tab.id"
          ref="tabButtons"
          type="button"
          role="tab"
          class="lw-c-tabs__tab"
          :class="{ 'lw-c-tabs__tab--selected': tab.id === modelValue }"
          :aria-selected="tab.id === modelValue ? 'true' : 'false'"
          :aria-controls="panelId(tab.id)"
          :tabindex="tab.id === modelValue ? 0 : -1"
          :data-tab="tab.id"
          @click="select(tab.id)"
        >
          <span class="lw-c-tabs__label">{{ tab.label }}</span>
          <span v-if="hasCount(tab)" class="lw-c-tabs__count">{{ tab.count }}</span>
        </button>
      </div>
    </div>

    <div
      v-for="tab in tabs"
      v-show="tab.id === modelValue"
      :id="panelId(tab.id)"
      :key="tab.id"
      role="tabpanel"
      class="lw-c-tabs__panel"
      :aria-labelledby="tabId(tab.id)"
      tabindex="0"
    >
      <slot :name="tab.id" />
    </div>
  </div>
</template>

<script>
import { provide, inject } from 'vue';

export default {
  name: 'LwTabs',
  props: {
    // [{ id, label, count? }]. `id` names the panel slot and is what
    // `modelValue` holds; keep it URL-safe, since pages put it in a query.
    tabs: {
      type: Array,
      required: true,
      validator: value =>
        value.length > 0 &&
        value.every(tab => tab && typeof tab.id === 'string' && typeof tab.label === 'string'),
    },
    modelValue: {
      type: String,
      required: true,
    },
    // The tablist's accessible name, e.g. "Dispatch sections".
    label: {
      type: String,
      required: true,
    },
    // Prefix for the tab and panel element ids. Two LwTabs on one page need
    // different prefixes or their ids collide.
    idPrefix: {
      type: String,
      default: 'lw-tabs',
    },
  },
  emits: ['update:modelValue'],
  // A tab row inside another tab row's content draws as segmented pills
  // (sass/_tab-row.scss). Whatever hosts a row of tabs provides the flag to
  // the content under it: this component for its panels, SectionLayout and
  // CustomersEdit for the pages their router tabs open.
  setup() {
    const nested = inject('lwTabsNested', false);
    provide('lwTabsNested', true);
    return { nested };
  },
  watch: {
    modelValue() {
      this.$nextTick(this.revealSelected);
    },
  },
  mounted() {
    this.revealSelected();
  },
  methods: {
    // On a narrow screen the tab row scrolls sideways, and a page opened on
    // its last tab (`?tab=batch`) would otherwise show that tab cut off or
    // off screen. Moves the row's own scroll only, never the page's, which
    // scrollIntoView would also do.
    revealSelected() {
      const scroller = this.$refs.scroller;
      const buttons = this.$refs.tabButtons || [];
      const target = buttons.find(el => el.dataset.tab === this.modelValue);
      if (!scroller || !target) return;
      // offsetLeft is measured from the scroller: it is position: relative,
      // which makes it the buttons' offsetParent.
      const left = target.offsetLeft;
      const right = left + target.offsetWidth;
      if (left < scroller.scrollLeft) {
        scroller.scrollLeft = left;
      } else if (right > scroller.scrollLeft + scroller.clientWidth) {
        scroller.scrollLeft = right - scroller.clientWidth;
      }
    },
    tabId(id) {
      return `${this.idPrefix}-tab-${id}`;
    },
    panelId(id) {
      return `${this.idPrefix}-panel-${id}`;
    },
    hasCount(tab) {
      return tab.count !== null && tab.count !== undefined;
    },
    select(id) {
      if (id !== this.modelValue) this.$emit('update:modelValue', id);
    },
    handleKeydown(event) {
      const ids = this.tabs.map(tab => tab.id);
      const current = Math.max(0, ids.indexOf(this.modelValue));
      let next;
      switch (event.key) {
        case 'ArrowRight':
          next = (current + 1) % ids.length;
          break;
        case 'ArrowLeft':
          next = (current - 1 + ids.length) % ids.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = ids.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.select(ids[next]);
      // Focus moves with the selection. The button already exists, so this
      // does not wait for the parent to write the new modelValue back.
      const buttons = this.$refs.tabButtons || [];
      const target = buttons.find(el => el.dataset.tab === ids[next]);
      if (target) target.focus();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/tab-row';

// The row is shared with LwSectionTabs (sass/_tab-row.scss), so a page's
// tabs and a section's tabs are one design.
.lw-c-tabs__list-scroller {
  @include lw-tab-row;
}

.lw-c-tabs__list {
  @include lw-tab-list;
}

.lw-c-tabs__tab {
  appearance: none;
  background: none;
  border: 0;
  @include lw-tab;
}

.lw-c-tabs__tab--selected {
  @include lw-tab-selected;
}

.lw-c-tabs__count {
  @include lw-tab-count;
}

.lw-c-tabs__tab--selected .lw-c-tabs__count {
  @include lw-tab-count-selected;
}

.lw-c-tabs--pills {
  .lw-c-tabs__list-scroller {
    @include lw-tab-pills-row;
  }

  .lw-c-tabs__list {
    @include lw-tab-pills-list;
  }

  .lw-c-tabs__tab {
    @include lw-tab-pill;
  }

  .lw-c-tabs__tab--selected {
    @include lw-tab-pill-selected;
  }
}

.lw-c-tabs__panel {
  padding-top: var(--lw-space-6);

  &:focus-visible {
    outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
    outline-offset: var(--lw-focus-ring-offset);
  }
}
</style>
